import { TaskCustomFieldModel } from "@jugl-web/rest-api/tasks/models/TaskCustomField";
import { useCallback } from "react";
import { useTaskBoardsQuery } from "./useTaskBoardsQuery";

interface UseTaskCustomFieldGettersOptions {
  entityId: string;
}

/**
 * Factories and functions that return board-level custom fields by their ID.
 * Designed to be used internally by the `useTaskCustomFields` hook but can also
 * be used by components which work outside of the board context.
 */
export const useTaskCustomFieldGetters = ({
  entityId,
}: UseTaskCustomFieldGettersOptions) => {
  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });

  const getCustomFieldsByBoard = useCallback(
    (boardId: string): TaskCustomFieldModel[] =>
      boardId ? getBoardByIdFromCache(boardId)?.customFields || [] : [],
    [getBoardByIdFromCache]
  );

  const produceGetCustomFieldById = useCallback(
    (boardId: string) => {
      const customFields = getCustomFieldsByBoard(boardId);
      const map = new Map(customFields.map((field) => [field.id, field]));

      return (fieldId: string) => map.get(fieldId);
    },
    [getCustomFieldsByBoard]
  );

  const getCustomFieldByBoard = useCallback(
    (boardId: string, fieldId: string) => {
      const getCustomFieldsById = produceGetCustomFieldById(boardId);
      return getCustomFieldsById(fieldId);
    },
    [produceGetCustomFieldById]
  );

  return {
    getCustomFieldsByBoard,
    produceGetCustomFieldById,
    getCustomFieldByBoard,
  };
};
